import { Link } from "react-router-dom";
import { BlogPageLayout } from "@/components/landing/BlogPageLayout";
import { SEO } from "@/components/SEO";
import { SchemaMarkup } from "@/components/seo/SchemaMarkup";
import { BreadcrumbNav } from "@/components/seo/BreadcrumbNav";
import { BlogFAQ } from "@/components/seo/BlogFAQ";
import { Calendar, Clock, ArrowRight, CheckCircle, AlertTriangle, FileText, Search, PenTool, LayoutTemplate, ScanLine, Users } from "lucide-react";
import { motion } from "framer-motion";

const BASE = "https://sorvaine.com";

const faqItems = [
  {
    question: "How long does an RFP response take?",
    answer: "Most RFP responses take 2–5 weeks depending on complexity, team size, and how much reusable content is available. AI-assisted workflows can reduce this to 3–7 days for standard proposals.",
  },
  {
    question: "What should be included in an RFP response?",
    answer: "A complete RFP response typically includes an executive summary, point-by-point answers to requirements, relevant case studies, team qualifications, a pricing section, and any compliance certifications the buyer requested.",
  },
  {
    question: "How do you structure an RFP response?",
    answer: "Follow the structure the buyer provides. Mirror their section numbering, address every requirement in order, and use clear headings. If no structure is given, organise by: executive summary, approach, experience, team, pricing, and appendices.",
  },
  {
    question: "Can AI help with RFP responses?",
    answer: "Yes. AI tools can extract requirements from RFP documents, search your content library for relevant answers, generate first drafts, and flag compliance gaps — reducing manual effort by 40–60%.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 16, filter: "blur(4px)" },
  visible: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as const } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.08 } },
};

function Section({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <motion.section
      variants={fadeUp}
      initial="visible"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className={className}
    >
      {children}
    </motion.section>
  );
}

export default function HowToRespondToAnRfp() {
  return (
    <BlogPageLayout>
      <SEO
        title="How to Respond to an RFP (Step-by-Step Guide)"
        description="Learn how to respond to an RFP step by step, from reviewing requirements to submitting a structured proposal."
        canonical={`${BASE}/resources/how-to-respond-to-an-rfp`}
      />
      <SchemaMarkup
        type="article"
        title="How to Respond to an RFP (Step-by-Step Guide)"
        description="Learn how to respond to an RFP step by step, from reviewing requirements to submitting a structured proposal."
        datePublished="2026-01-22"
        url={`${BASE}/resources/how-to-respond-to-an-rfp`}
      />
      <SchemaMarkup type="faq" items={faqItems} />

      <article className="pt-28 pb-20 max-w-[900px] mx-auto px-6">
        <BreadcrumbNav
          items={[
            { name: "Home", url: BASE },
            { name: "Resources", url: `${BASE}/resources` },
            { name: "How to Respond to an RFP", url: `${BASE}/resources/how-to-respond-to-an-rfp` },
          ]}
        />

        {/* HERO — Variation: Dark background band behind title */}
        <motion.div initial="hidden" animate="visible" variants={stagger}>
          <motion.div variants={fadeUp} className="flex items-center gap-3 mb-5">
            <span className="text-[11px] font-semibold tracking-[0.15em] uppercase text-primary/80">Guide</span>
            <span className="flex items-center gap-1 text-[11px] text-gray-400">
              <Calendar className="w-3 h-3" /> 22 Jan 2026
            </span>
            <span className="flex items-center gap-1 text-[11px] text-gray-400">
              <Clock className="w-3 h-3" /> 6 min read
            </span>
          </motion.div>

          <motion.div variants={fadeUp} className="bg-gray-900 rounded-lg px-8 py-7 -mx-2 mb-2">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white leading-[1.08]">
              How to Respond to an RFP
            </h1>
            <span className="block text-xl md:text-2xl font-medium text-white/50 mt-2">Step-by-Step Guide</span>
          </motion.div>

          <motion.p variants={fadeUp} className="mt-5 text-lg text-gray-500 max-w-[620px] leading-relaxed">
            RFPs are time-consuming, detail-heavy, and unforgiving. This guide breaks the response process into clear steps — from initial review to final submission — so your team can work faster without missing anything.
          </motion.p>
        </motion.div>

        <div className="mt-14 space-y-16">

          {/* INTRO HOOK */}
          <Section>
            <p className="text-[15px] text-gray-500 leading-[1.75]">
              Most proposal teams follow roughly the same process: read the RFP, figure out what's being asked, dig up past answers, draft new ones, stitch everything together, and submit before the deadline. The problem isn't the process — it's the friction at every stage. Manual document review, scattered content, and last-minute formatting eat up the majority of response time. This guide walks through each step clearly, so you can identify where your team loses time and where modern tools can help.
            </p>
          </Section>

          {/* SECTION 1: What is an RFP Response */}
          <Section>
            <h2 className="text-2xl font-semibold tracking-tight text-gray-900">What is an RFP response?</h2>
            <p className="mt-4 text-[15px] text-gray-500 leading-[1.75]">
              An RFP response is the formal document your team submits to answer a buyer's Request for Proposal. It's your opportunity to demonstrate that you understand the requirement, have the capability to deliver, and offer competitive terms.
            </p>
            <p className="mt-3 text-[15px] text-gray-500 leading-[1.75]">
              Buyers use RFP responses to compare suppliers on a level playing field. They expect:
            </p>
            <ul className="mt-3 space-y-2 text-[15px] text-gray-500 leading-[1.75]">
              <li className="flex items-start gap-2.5">
                <CheckCircle className="w-4 h-4 mt-1 text-primary/60 shrink-0" />
                <span><strong className="text-gray-700">Clear, direct answers</strong> to every question and requirement they've listed.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <CheckCircle className="w-4 h-4 mt-1 text-primary/60 shrink-0" />
                <span><strong className="text-gray-700">Compliance evidence</strong> — proof that you meet mandatory criteria.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <CheckCircle className="w-4 h-4 mt-1 text-primary/60 shrink-0" />
                <span><strong className="text-gray-700">Relevant experience</strong> — case studies, references, and team credentials that match the scope.</span>
              </li>
            </ul>
          </Section>

          {/* SECTION 2: Step-by-Step Process */}
          <Section>
            <h2 className="text-2xl font-semibold tracking-tight text-gray-900">Step-by-step RFP response process</h2>
            <p className="mt-4 text-[15px] text-gray-500 leading-[1.75]">
              This is how experienced teams approach RFP responses. Each step builds on the last — skip one and you'll feel it later.
            </p>
          </Section>

          {/* Step 1 */}
          <Section>
            <div className="pl-5 border-l-2 border-primary/20">
              <span className="text-[11px] font-mono text-primary/50 tracking-wider uppercase">Step 1</span>
              <h3 className="mt-1 text-xl font-semibold tracking-tight text-gray-900">Review the RFP requirements</h3>
              <p className="mt-3 text-[15px] text-gray-500 leading-[1.75]">
                Before assigning anything, one person should read the full RFP end-to-end. The goal isn't to start answering — it's to understand what's being asked and how you'll be evaluated.
              </p>
              <ul className="mt-3 space-y-2 text-[15px] text-gray-500 leading-[1.75]">
                <li className="flex items-start gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary/40 mt-2.5 shrink-0" />
                  <span>Read the full document — don't skim. Requirements hide in appendices and footnotes.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary/40 mt-2.5 shrink-0" />
                  <span>Identify key sections: scope, evaluation criteria, mandatory requirements, submission format.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary/40 mt-2.5 shrink-0" />
                  <span>Understand how evaluators will score — this shapes your writing priorities.</span>
                </li>
              </ul>
            </div>
          </Section>

          {/* Step 2 */}
          <Section>
            <div className="pl-5 border-l-2 border-primary/20">
              <span className="text-[11px] font-mono text-primary/50 tracking-wider uppercase">Step 2</span>
              <h3 className="mt-1 text-xl font-semibold tracking-tight text-gray-900">Extract key questions and requirements</h3>
              <p className="mt-3 text-[15px] text-gray-500 leading-[1.75]">
                Pull every question, requirement, and compliance criterion into a single list. This becomes your response framework.
              </p>
              <ul className="mt-3 space-y-2 text-[15px] text-gray-500 leading-[1.75]">
                <li className="flex items-start gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary/40 mt-2.5 shrink-0" />
                  <span>List all questions — even ones buried in tables or appendices.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary/40 mt-2.5 shrink-0" />
                  <span>Highlight compliance items — certifications, insurance, regulatory standards.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary/40 mt-2.5 shrink-0" />
                  <span>Separate mandatory requirements from optional "nice-to-haves."</span>
                </li>
              </ul>
            </div>
          </Section>

          {/* VISUAL BLOCK: Manual vs AI Extraction */}
          <Section>
            <h3 className="text-lg font-semibold tracking-tight text-gray-900 mb-4">Manual vs AI requirement extraction</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-2 h-2 rounded-full bg-gray-300" />
                  <span className="text-xs font-semibold tracking-wider uppercase text-gray-400">Manual</span>
                </div>
                <ul className="space-y-3">
                  {[
                    "Scan each page of the RFP by hand",
                    "Risk missing requirements in annexes",
                    "Manual list-building in spreadsheets",
                    "No structured output — just notes",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-gray-500 leading-relaxed">
                      <span className="text-[11px] font-mono text-gray-300 mt-0.5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-xl border border-primary/20 bg-primary/[0.03] p-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-2 h-2 rounded-full bg-primary/60" />
                  <span className="text-xs font-semibold tracking-wider uppercase text-primary/70">AI-assisted</span>
                </div>
                <ul className="space-y-3">
                  {[
                    "Upload RFP — requirements extracted automatically",
                    "Compliance criteria flagged and categorised",
                    "Structured requirement list in seconds",
                    "Nothing missed — including appendices",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-gray-600 leading-relaxed">
                      <span className="text-[11px] font-mono text-primary/50 mt-0.5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Section>

          {/* Step 3 */}
          <Section>
            <div className="pl-5 border-l-2 border-primary/20">
              <span className="text-[11px] font-mono text-primary/50 tracking-wider uppercase">Step 3</span>
              <h3 className="mt-1 text-xl font-semibold tracking-tight text-gray-900">Gather relevant information</h3>
              <p className="mt-3 text-[15px] text-gray-500 leading-[1.75]">
                Before writing, assemble the raw materials. The biggest time sink in RFP response is searching — not writing.
              </p>
              <ul className="mt-3 space-y-2 text-[15px] text-gray-500 leading-[1.75]">
                <li className="flex items-start gap-2.5">
                  <Search className="w-4 h-4 mt-1 text-primary/50 shrink-0" />
                  <span><strong className="text-gray-700">Past responses</strong> — find previous answers that address similar requirements.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <FileText className="w-4 h-4 mt-1 text-primary/50 shrink-0" />
                  <span><strong className="text-gray-700">Internal documentation</strong> — technical specs, policies, process descriptions.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <Users className="w-4 h-4 mt-1 text-primary/50 shrink-0" />
                  <span><strong className="text-gray-700">Stakeholder input</strong> — subject-matter experts who own specific areas.</span>
                </li>
              </ul>
            </div>
          </Section>

          {/* Step 4 */}
          <Section>
            <div className="pl-5 border-l-2 border-primary/20">
              <span className="text-[11px] font-mono text-primary/50 tracking-wider uppercase">Step 4</span>
              <h3 className="mt-1 text-xl font-semibold tracking-tight text-gray-900">Draft responses</h3>
              <p className="mt-3 text-[15px] text-gray-500 leading-[1.75]">
                Write clear, direct answers to every requirement. Evaluators read dozens of proposals — they reward clarity over cleverness.
              </p>
              <ul className="mt-3 space-y-2 text-[15px] text-gray-500 leading-[1.75]">
                <li className="flex items-start gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary/40 mt-2.5 shrink-0" />
                  <span>Answer the question directly in the first sentence, then expand with evidence.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary/40 mt-2.5 shrink-0" />
                  <span>Maintain a consistent tone across all contributors.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary/40 mt-2.5 shrink-0" />
                  <span>Align every answer with the evaluation criteria — not just the question.</span>
                </li>
              </ul>
            </div>
          </Section>

          {/* Step 5 */}
          <Section>
            <div className="pl-5 border-l-2 border-primary/20">
              <span className="text-[11px] font-mono text-primary/50 tracking-wider uppercase">Step 5</span>
              <h3 className="mt-1 text-xl font-semibold tracking-tight text-gray-900">Structure the proposal</h3>
              <p className="mt-3 text-[15px] text-gray-500 leading-[1.75]">
                Organisation matters as much as content. A well-structured proposal is easier to evaluate — and easier proposals score higher.
              </p>
              <ul className="mt-3 space-y-2 text-[15px] text-gray-500 leading-[1.75]">
                <li className="flex items-start gap-2.5">
                  <LayoutTemplate className="w-4 h-4 mt-1 text-primary/50 shrink-0" />
                  <span>Mirror the RFP's section structure wherever possible.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <LayoutTemplate className="w-4 h-4 mt-1 text-primary/50 shrink-0" />
                  <span>Use clear headings, numbered sections, and consistent formatting.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <LayoutTemplate className="w-4 h-4 mt-1 text-primary/50 shrink-0" />
                  <span>Follow submission format requirements exactly — page limits, file types, naming conventions.</span>
                </li>
              </ul>
            </div>
          </Section>

          {/* Step 6 */}
          <Section>
            <div className="pl-5 border-l-2 border-primary/20">
              <span className="text-[11px] font-mono text-primary/50 tracking-wider uppercase">Step 6</span>
              <h3 className="mt-1 text-xl font-semibold tracking-tight text-gray-900">Review and refine</h3>
              <p className="mt-3 text-[15px] text-gray-500 leading-[1.75]">
                The review stage catches the mistakes that lose bids. Don't skip it — budget at least 20% of your total response time here.
              </p>
              <ul className="mt-3 space-y-2 text-[15px] text-gray-500 leading-[1.75]">
                <li className="flex items-start gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary/40 mt-2.5 shrink-0" />
                  <span>Check compliance: does your response address every mandatory requirement?</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary/40 mt-2.5 shrink-0" />
                  <span>Identify gaps: are there questions you've answered weakly or incompletely?</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary/40 mt-2.5 shrink-0" />
                  <span>Quality pass: readability, consistency, tone, and accuracy across all sections.</span>
                </li>
              </ul>
            </div>
          </Section>

          {/* Why It Takes So Long */}
          <Section>
            <h2 className="text-2xl font-semibold tracking-tight text-gray-900">Why this process is so time-consuming</h2>
            <p className="mt-4 text-[15px] text-gray-500 leading-[1.75]">
              The steps above are logical — the problem is execution. Here's where teams actually lose time:
            </p>
            <ul className="mt-4 space-y-3 text-[15px] text-gray-500 leading-[1.75]">
              {[
                "Repetitive work — the same questions appear across RFPs, but teams redraft answers from scratch.",
                "Searching for answers — past responses live in scattered folders, emails, and people's heads.",
                "Formatting — assembling a consistent document from multiple contributors is tedious.",
                "Coordination — chasing SMEs for input, managing deadlines, and resolving conflicting drafts.",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <AlertTriangle className="w-4 h-4 mt-1 text-amber-500/70 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Section>

          {/* Modern Approach */}
          <Section>
            <h2 className="text-2xl font-semibold tracking-tight text-gray-900">A faster way to respond to RFPs</h2>
            <p className="mt-4 text-[15px] text-gray-500 leading-[1.75]">
              Modern proposal teams are moving away from the manual workflow described above. Instead of reading, searching, and copy-pasting, they use AI-powered platforms to handle the repetitive layers of the process.
            </p>
            <p className="mt-3 text-[15px] text-gray-500 leading-[1.75]">
              The core shift: AI handles extraction, content matching, and first-draft generation — your team focuses on strategy, tailoring, and review. This doesn't replace expertise. It removes the busywork that sits between your experts and the final document.
            </p>
            <div className="mt-6 grid sm:grid-cols-3 gap-4">
              {[
                { icon: ScanLine, label: "Extract requirements instantly", desc: "Upload the RFP and get a structured list of every question and compliance criterion." },
                { icon: PenTool, label: "Generate structured drafts", desc: "AI drafts responses using your content library, calibrated to the buyer's context." },
                { icon: CheckCircle, label: "Highlight missing gaps", desc: "Compliance mapping shows what's covered and what still needs attention." },
              ].map((item, i) => (
                <div key={i} className="rounded-lg border border-gray-200 bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
                  <span className="flex items-center justify-center w-8 h-8 rounded-md bg-primary/10 text-primary mb-3">
                    <item.icon className="w-4 h-4" />
                  </span>
                  <h3 className="text-sm font-semibold text-gray-800">{item.label}</h3>
                  <p className="mt-1 text-[13px] text-gray-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </Section>

          {/* Product CTA Block */}
          <Section>
            <div className="rounded-xl border border-gray-200 bg-white p-8 md:p-10 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
              <div className="text-center">
                <h3 className="text-xl font-semibold tracking-tight text-gray-900">See how this works in practice</h3>
                <p className="mt-2 text-gray-500 max-w-md mx-auto">Turn an RFP into a structured draft in minutes — not weeks.</p>
                <div className="mt-5">
                  <Link to="/book-demo">
                    <button className="inline-flex items-center justify-center px-8 h-11 rounded-md bg-primary hover:bg-primary/90 text-white text-sm font-semibold transition-all duration-200 hover:-translate-y-[1px] active:scale-[0.97] shadow-[0_0_16px_hsl(var(--primary)/0.12)]">
                      See How It Works
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          </Section>

          {/* Common Mistakes */}
          <Section>
            <h2 className="text-2xl font-semibold tracking-tight text-gray-900">Common mistakes to avoid</h2>
            <div className="mt-5 space-y-4">
              {[
                { title: "Missing requirements", desc: "Failing to answer every mandatory question is the fastest way to get disqualified. Always cross-reference your response against the full requirement list." },
                { title: "Inconsistent answers", desc: "When multiple contributors draft sections independently, tone, terminology, and even facts can conflict. A single review pass catches this." },
                { title: "Poor structure", desc: "Evaluators expect your response to follow the RFP's section order. Deviating makes it harder to score — and harder means lower marks." },
                { title: "Rushing the submission", desc: "Leaving review and formatting to the last day guarantees errors. Build in buffer time — at least two working days before the deadline." },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-4 rounded-lg border border-gray-100 bg-gray-50/50">
                  <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-500/70 shrink-0" />
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700">{item.title}</h3>
                    <p className="mt-0.5 text-[13px] text-gray-500 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* Key Takeaways */}
          <Section>
            <h2 className="text-2xl font-semibold tracking-tight text-gray-900">Key takeaways</h2>
            <div className="mt-5 rounded-xl border border-gray-200 bg-gray-50 p-6">
              <ul className="space-y-3 text-[15px] text-gray-600 leading-[1.75]">
                {[
                  "RFP responses follow a structured, repeatable process — review, extract, gather, draft, structure, review.",
                  "The biggest time sinks are searching for content and coordinating across contributors.",
                  "Manual workflows are slow and error-prone, especially at scale.",
                  "AI-assisted tools reduce response time by 40–60% by automating extraction and drafting.",
                  "The best results come from combining AI efficiency with human editorial judgement.",
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <CheckCircle className="w-4 h-4 mt-1 text-primary/60 shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Section>

          <Section>
            <BlogFAQ items={faqItems} />
          </Section>

          {/* Internal Links */}
          <Section>
            <h2 className="text-xl font-semibold text-gray-800">Related reading</h2>
            <ul className="mt-4 space-y-2">
              <li><Link to="/rfp-response-software" className="text-sm text-primary hover:underline">RFP Response Software — Full Platform Overview →</Link></li>
              <li><Link to="/resources/what-is-rfp-response-software" className="text-sm text-primary hover:underline">What Is RFP Response Software?</Link></li>
              <li><Link to="/loopio-alternatives" className="text-sm text-primary hover:underline">Top Loopio Alternatives Compared</Link></li>
              <li><Link to="/resources/common-rfp-mistakes" className="text-sm text-primary hover:underline">7 Common RFP Mistakes (And How to Fix Them)</Link></li>
            </ul>
          </Section>

          {/* Final CTA */}
          <Section>
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-8 md:p-12 text-center">
              <h2 className="text-2xl font-semibold tracking-tight text-gray-900">Want to speed up your RFP responses?</h2>
              <p className="mt-3 text-gray-500 max-w-lg mx-auto">Book a demo and see how Sorvaine helps teams create structured proposals faster.</p>
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link to="/book-demo">
                  <button className="inline-flex items-center justify-center px-8 h-11 rounded-md bg-primary hover:bg-primary/90 text-white text-sm font-semibold transition-all duration-200 hover:-translate-y-[1px] active:scale-[0.97] shadow-[0_0_16px_hsl(var(--primary)/0.12)]">
                    Book a Demo
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </button>
                </Link>
                <Link to="/rfp-response-software">
                  <button className="inline-flex items-center justify-center px-8 h-11 rounded-md border border-gray-200 hover:border-gray-300 text-gray-600 hover:text-gray-900 text-sm font-semibold transition-all duration-200 active:scale-[0.97]">
                    Learn More
                  </button>
                </Link>
              </div>
            </div>
          </Section>
        </div>
      </article>
    </BlogPageLayout>
  );
}
